import { X, Star, ExternalLink, Mail, Phone, Building2, Globe, MapPin, Trophy, Calendar, ArrowRight, Loader2, Lock, Unlock } from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import type { Lead } from './LeadsTable';

const STAGE_OPTIONS = [
  { value: 'new', label: 'Nouveau', color: 'bg-gray-100 text-gray-700' },
  { value: 'contacted', label: 'Contacte', color: 'bg-blue-100 text-blue-700' },
  { value: 'replied', label: 'Repondu', color: 'bg-amber-100 text-amber-700' },
  { value: 'meeting', label: 'RDV', color: 'bg-teal-100 text-teal-700' },
  { value: 'deal', label: 'Deal', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'won', label: 'Gagne', color: 'bg-green-100 text-green-700' },
  { value: 'lost', label: 'Perdu', color: 'bg-red-100 text-red-700' },
];

interface Activity {
  id: string;
  type: string;
  user_name: string;
  payload_json: Record<string, string>;
  created_at: string;
}

interface LeadSidePanelProps {
  lead: Lead | null;
  onClose: () => void;
  onToggleTop: (leadId: string) => void;
  onStageChange: (leadId: string, stage: string) => void;
  onNoteSave: (leadId: string, notes: string) => void;
  agencyId: string;
}

export default function LeadSidePanel({
  lead,
  onClose,
  onToggleTop,
  onStageChange,
  onNoteSave,
  agencyId,
}: LeadSidePanelProps) {
  const [notes, setNotes] = useState('');
  const [activities, setActivities] = useState<Activity[]>([]);
  const [savingNote, setSavingNote] = useState(false);
  const [unlockedEmails, setUnlockedEmails] = useState<Set<string>>(new Set());
  const [unlockingEmail, setUnlockingEmail] = useState(false);

  useEffect(() => {
    if (lead) {
      setNotes(lead.notes || '');
      loadActivities(lead.id);
    }
  }, [lead?.id]);

  useEffect(() => {
    if (agencyId) {
      loadUnlockedEmails();
    }
  }, [agencyId]);

  const loadUnlockedEmails = async () => {
    const { data } = await supabase
      .from('unlocked_lead_emails')
      .select('email')
      .eq('organization_id', agencyId);

    if (data) {
      setUnlockedEmails(new Set(data.map(item => item.email)));
    }
  };

  const loadActivities = async (leadId: string) => {
    const { data } = await supabase
      .from('lead_activity')
      .select('*')
      .eq('lead_id', leadId)
      .order('created_at', { ascending: false })
      .limit(20);
    setActivities(data || []);
  };

  if (!lead) return null;

  const handleSaveNote = async () => {
    setSavingNote(true);
    await onNoteSave(lead.id, notes);
    setSavingNote(false);
  };

  const handleUnlockEmail = async (email: string) => {
    if (unlockingEmail) return;

    if (!agencyId) {
      alert('Impossible de recuperer vos informations. Veuillez rafraichir la page.');
      return;
    }

    setUnlockingEmail(true);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      if (!token) {
        alert('Session expirée. Veuillez vous reconnecter.');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/unlock-lead-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            agencyId,
            email,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        if (response.status === 402) {
          alert('Credits insuffisants. Vous avez besoin de 1 credit pour debloquer cet email.');
        } else {
          alert('Erreur: ' + (result.error || 'Une erreur est survenue'));
        }
        return;
      }

      setUnlockedEmails(prev => new Set(prev).add(email));
    } catch (error) {
      console.error('Unexpected error:', error);
      alert('Erreur lors du deblocage de l\'email');
    } finally {
      setUnlockingEmail(false);
    }
  };

  const blurEmail = (email: string) => {
    const [local, domain] = email.split('@');
    if (!domain) return '***@***';
    const blurredLocal = '*'.repeat(Math.max(local.length, 3));
    return `${blurredLocal}@${domain}`;
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl border-l border-gray-200 z-40 flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h3 className="font-bold text-gray-900">Detail du lead</h3>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <X className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="text-lg font-bold text-gray-900">{lead.contact_first_name} {lead.contact_last_name}</h4>
              <p className="text-sm text-gray-500">{lead.job_title}</p>
            </div>
            <button
              onClick={() => onToggleTop(lead.id)}
              className={`p-2 rounded-lg transition-colors ${
                lead.is_top_contact ? 'bg-amber-50 text-amber-500' : 'bg-gray-50 text-gray-400 hover:bg-amber-50 hover:text-amber-500'
              }`}
            >
              <Star className={`w-5 h-5 ${lead.is_top_contact ? 'fill-amber-400' : ''}`} />
            </button>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Building2 className="w-4 h-4 text-gray-400" />
            <span className="font-medium">{lead.company_name}</span>
            {lead.company_size && <span className="text-gray-400">({lead.company_size})</span>}
          </div>

          {lead.company_website && (
            <a href={lead.company_website.startsWith('http') ? lead.company_website : `https://${lead.company_website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700">
              <Globe className="w-4 h-4" />
              {lead.company_domain || lead.company_website}
            </a>
          )}

          {lead.company_country && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <MapPin className="w-4 h-4 text-gray-400" />
              {lead.company_country}
            </div>
          )}
        </div>

        <div className="space-y-2">
          {lead.email && (
            <div className="space-y-2">
              {unlockedEmails.has(lead.email) ? (
                <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600 transition-colors">
                  <Unlock className="w-4 h-4 text-green-500" />
                  {lead.email}
                </a>
              ) : (
                <div className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Lock className="w-4 h-4 text-gray-400" />
                    <span className="font-mono">{blurEmail(lead.email)}</span>
                  </div>
                  <button
                    onClick={() => handleUnlockEmail(lead.email)}
                    disabled={unlockingEmail}
                    className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {unlockingEmail ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Débloquer (1 crédit)'}
                  </button>
                </div>
              )}
            </div>
          )}
          {lead.phone && (
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Phone className="w-4 h-4 text-gray-400" />
              {lead.phone}
            </div>
          )}
          {lead.linkedin_url && (
            <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700">
              <ExternalLink className="w-4 h-4" />
              Profil LinkedIn
            </a>
          )}
        </div>

        <div className="space-y-3">
          <h5 className="text-sm font-semibold text-gray-700">Actions</h5>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onToggleTop(lead.id)}
              className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-gray-700"
            >
              <Star className="w-3.5 h-3.5" />
              {lead.is_top_contact ? 'Retirer Top' : 'Top contact'}
            </button>
            <button
              onClick={() => onStageChange(lead.id, 'meeting')}
              className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-teal-200 bg-teal-50 hover:bg-teal-100 transition-colors text-teal-700"
            >
              <Calendar className="w-3.5 h-3.5" />
              Passer en RDV
            </button>
            <div className="flex gap-1">
              <button
                onClick={() => onStageChange(lead.id, 'won')}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-2 text-xs font-medium rounded-lg border border-green-200 bg-green-50 hover:bg-green-100 transition-colors text-green-700"
              >
                <Trophy className="w-3.5 h-3.5" />
                Gagne
              </button>
              <button
                onClick={() => onStageChange(lead.id, 'lost')}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-2 text-xs font-medium rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 transition-colors text-red-700"
              >
                Perdu
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <h5 className="text-sm font-semibold text-gray-700">Pipeline</h5>
          <div className="flex flex-wrap gap-1.5">
            {STAGE_OPTIONS.map((s) => (
              <button
                key={s.value}
                onClick={() => onStageChange(lead.id, s.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  lead.stage === s.value ? s.color + ' ring-2 ring-offset-1 ring-gray-300' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-semibold text-gray-700">Notes internes</h5>
            <button
              onClick={handleSaveNote}
              disabled={savingNote}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
            >
              {savingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Sauvegarder'}
            </button>
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Ajouter des notes..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 outline-none resize-none"
          />
        </div>

        <div className="space-y-3">
          <h5 className="text-sm font-semibold text-gray-700">Historique</h5>
          {activities.length === 0 ? (
            <p className="text-xs text-gray-400">Aucune activite</p>
          ) : (
            <div className="space-y-2">
              {activities.map((a) => (
                <div key={a.id} className="flex items-start gap-2 text-xs">
                  <ArrowRight className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-gray-600">
                      {a.type === 'stage_change' && `Stage: ${a.payload_json?.from || '?'} → ${a.payload_json?.to || '?'}`}
                      {a.type === 'note' && 'Note ajoutee'}
                      {a.type === 'assignment' && `Assigne a ${a.payload_json?.to || '?'}`}
                      {a.type === 'import' && 'Importe'}
                    </span>
                    <span className="text-gray-400 ml-2">{a.user_name} - {new Date(a.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
